import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, PlusCircle, Edit3, Trash2, Eye, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export const AdminBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/blog', { credentials: 'include' });
      const d = await r.json();
      setPosts(d.posts || []);
    } catch (e) {
      toast.error('Beiträge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate, load]);

  const deletePost = async (id, title) => {
    if (!window.confirm(`Beitrag „${title}" wirklich löschen?`)) return;
    try {
      const r = await fetch(apiBase() + `/api/admin/blog/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error('Fehler');
      toast.success('Beitrag gelöscht');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/dashboard" className="text-gray-400 hover:text-[#C4A55A]" data-testid="admin-blog-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <FileText className="h-6 w-6 text-[#C4A55A]" />
          <h1 className="text-xl font-semibold">Blog-Verwaltung</h1>
          <Link
            to="/admin/blog/new"
            data-testid="admin-blog-new"
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold text-sm"
          >
            <PlusCircle className="h-4 w-4" /> Neuer Beitrag
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {loading ? (
          <div className="text-gray-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
        ) : posts.length === 0 ? (
          <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-12 text-center">
            <FileText className="h-12 w-12 text-[#C4A55A]/40 mx-auto mb-3" />
            <h2 className="text-lg font-semibold mb-2">Noch keine Beiträge</h2>
            <p className="text-sm text-gray-400 mb-4">Schreibe Deinen ersten Beitrag, um den Blog mit Leben zu füllen.</p>
            <Link
              to="/admin/blog/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 text-sm"
            >
              <PlusCircle className="h-4 w-4" /> Ersten Beitrag erstellen
            </Link>
          </div>
        ) : (
          <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-blog-table">
              <thead className="bg-black/40 text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4 text-left">Titel</th>
                  <th className="py-3 px-4 text-left">Slug</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Aufrufe</th>
                  <th className="py-3 px-4 text-right">Erstellt</th>
                  <th className="py-3 px-4 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`blog-row-${p.slug}`}>
                    <td className="py-3 px-4 font-medium">{p.title}</td>
                    <td className="py-3 px-4 text-gray-400 text-xs font-mono">{p.slug}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        p.status === 'published'
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                          : 'bg-gray-500/10 border-gray-500/30 text-gray-400'
                      }`}>
                        {p.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-gray-400">{p.viewCount || 0}</td>
                    <td className="py-3 px-4 text-right text-xs text-gray-500">
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString('de-CH') : '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {p.status === 'published' && (
                          <a
                            href={`/blog/${p.slug}`}
                            target="_blank" rel="noopener noreferrer"
                            title="Im Blog ansehen"
                            data-testid={`blog-view-${p.slug}`}
                            className="p-1.5 rounded hover:bg-white/10 text-gray-300"
                          >
                            <Eye className="h-4 w-4" />
                          </a>
                        )}
                        <Link
                          to={`/admin/blog/${p.id}/edit`}
                          title="Bearbeiten"
                          data-testid={`blog-edit-${p.slug}`}
                          className="p-1.5 rounded hover:bg-white/10 text-[#C4A55A]"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => deletePost(p.id, p.title)}
                          title="Löschen"
                          data-testid={`blog-delete-${p.slug}`}
                          className="p-1.5 rounded hover:bg-white/10 text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};
